import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/axios";

export default function Login() {
  const [mode, setMode] = useState("password"); // "password" | "otp"

  // Password login state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // MFA state (super admin 2FA)
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [mfaOtp, setMfaOtp] = useState("");
  const [mfaSentTo, setMfaSentTo] = useState(null);
  const [mfaTimer, setMfaTimer] = useState(0);

  // OTP login state
  const [otpUsername, setOtpUsername] = useState("");
  const [otp, setOtp] = useState("");
  const [otpStep, setOtpStep] = useState(1);
  const [otpSentTo, setOtpSentTo] = useState(null);
  const [otpTimer, setOtpTimer] = useState(0);

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  const { login, loginWithOtp, setAuthData } = useAuth();
  const navigate = useNavigate();

  function redirectUser(user) {
    if (user.mustResetPassword) { navigate("/reset-password"); return; }
    if (user.role === "super_admin")       navigate("/super-admin");
    else if (user.role === "school_admin") navigate("/school-admin");
    else if (user.role === "teacher")      navigate("/teacher");
  }

  function startTimer(setter) {
    setter(60);
    const iv = setInterval(() => {
      setter((t) => { if (t <= 1) { clearInterval(iv); return 0; } return t - 1; });
    }, 1000);
  }

  // ── Password login ──────────────────────────────────────────────────────────
  async function handlePasswordSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      if (data.mfa_required) {
        setMfaToken(data.mfa_token);
        setMfaSentTo(data.sentTo);
        setMfaRequired(true);
        setMfaOtp("");
        startTimer(setMfaTimer);
        return;
      }
      // Non-super-admin: token returned directly
      setAuthData(data.token, data.user);
      redirectUser(data.user);
    } catch (err) {
      setError(err.response?.data?.error || "Invalid username or password.");
    } finally {
      setLoading(false);
    }
  }

  // ── MFA verify ──────────────────────────────────────────────────────────────
  async function handleMfaSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-mfa", { mfa_token: mfaToken, otp: mfaOtp });
      setAuthData(data.token, data.user);
      redirectUser(data.user);
    } catch (err) {
      setError(err.response?.data?.error || "Invalid or expired OTP.");
    } finally {
      setLoading(false);
    }
  }

  async function resendMfaOtp() {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      if (data.mfa_required) {
        setMfaToken(data.mfa_token);
        setMfaSentTo(data.sentTo);
        setMfaOtp("");
        startTimer(setMfaTimer);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Failed to resend OTP.");
    } finally {
      setLoading(false);
    }
  }

  // ── OTP login Step 1 ────────────────────────────────────────────────────────
  async function handleRequestOtp(e) {
    e.preventDefault();
    setError(""); setInfo(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/request-otp", { username: otpUsername });
      setOtpSentTo(data.sentTo);
      setOtpStep(2);
      setInfo(data.message);
      startTimer(setOtpTimer);
    } catch (err) {
      setError(err.response?.data?.error || "Could not send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // ── OTP login Step 2 ────────────────────────────────────────────────────────
  async function handleVerifyOtp(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = await loginWithOtp(otpUsername, otp);
      redirectUser(user);
    } catch (err) {
      setError(err.response?.data?.error || "Invalid or expired OTP.");
    } finally {
      setLoading(false);
    }
  }

  function switchMode(m) {
    setMode(m); setError(""); setInfo(""); setOtpStep(1); setOtp(""); setOtpSentTo(null);
  }

  return (
    <div className="login-page">
      {/* Left brand panel */}
      <div className="login-brand-panel">
        <div className="logo-on-dark" style={{ padding: "10px 14px", borderRadius: 16, marginBottom: 20 }}>
          <img src="/logo.png" alt="Volcanic Education and Technology" className="logo-img-lg" />
        </div>
        <div className="login-brand-title">Volcanic Result Management System</div>
        <div className="login-brand-tagline">A unit of Volcanic Education and Technology Pvt Ltd</div>
        <div style={{ marginTop: 8, marginBottom: 4, fontSize: 13, color: "rgba(255,255,255,0.45)" }}>
          The complete result management platform for modern Indian schools.
        </div>
        <div className="login-brand-features">
          <div className="login-brand-feature"><span>🏫</span> Multi-school SaaS platform</div>
          <div className="login-brand-feature"><span>⏱️</span> Time-locked marks entry</div>
          <div className="login-brand-feature"><span>📄</span> Automated PDF result cards</div>
          <div className="login-brand-feature"><span>🔒</span> Secure student portal</div>
          <div className="login-brand-feature"><span>📊</span> Excel export &amp; analytics</div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="login-form-panel">
        <div className="login-box">
          <h1>Welcome back</h1>
          <p className="subtitle" style={{ marginBottom: 20 }}>Sign in to your account</p>

          {/* ── MFA screen ── */}
          {mfaRequired ? (
            <form onSubmit={handleMfaSubmit}>
              <div style={{ background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 8, padding: "14px 16px", marginBottom: 20, fontSize: 13, color: "#0369A1" }}>
                <strong>Two-Factor Authentication</strong>
                <p style={{ margin: "6px 0 0" }}>A verification code has been sent to:</p>
                {mfaSentTo?.email  && <span style={{ display: "block" }}>📧 {mfaSentTo.email}</span>}
                {mfaSentTo?.mobile && <span style={{ display: "block" }}>📱 {mfaSentTo.mobile}</span>}
              </div>
              <div style={{ marginBottom: 20 }}>
                <label className="form-label">Enter 6-digit OTP</label>
                <input
                  placeholder="• • • • • •"
                  value={mfaOtp}
                  onChange={(e) => setMfaOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required autoFocus inputMode="numeric"
                  style={{ fontSize: 22, letterSpacing: 8, textAlign: "center" }}
                />
                <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--muted)" }}>Valid for 10 minutes.</p>
              </div>
              <button className="btn" style={{ width: "100%", marginBottom: 10 }} type="submit" disabled={loading || mfaOtp.length < 6}>
                {loading ? "Verifying..." : "Verify & Sign In"}
              </button>
              <button type="button" className="btn secondary" style={{ width: "100%", fontSize: 13 }} onClick={resendMfaOtp} disabled={mfaTimer > 0 || loading}>
                {mfaTimer > 0 ? `Resend OTP in ${mfaTimer}s` : "Resend OTP"}
              </button>
              <button type="button" style={{ marginTop: 10, width: "100%", background: "none", border: "none", color: "var(--muted)", fontSize: 13, cursor: "pointer" }}
                onClick={() => { setMfaRequired(false); setMfaOtp(""); setError(""); }}>
                ← Back to Login
              </button>
              {error && <div className="error-text">{error}</div>}
            </form>
          ) : (
            <>
              {/* Mode toggle */}
              <div style={{ display: "flex", gap: 0, marginBottom: 24, border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }}>
                <button type="button" onClick={() => switchMode("password")} style={{
                  flex: 1, padding: "9px 0", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer",
                  background: mode === "password" ? "#1E3A5F" : "#F8FAFC",
                  color: mode === "password" ? "#fff" : "#64748B", transition: "background 0.15s",
                }}>Password Login</button>
                <button type="button" onClick={() => switchMode("otp")} style={{
                  flex: 1, padding: "9px 0", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer",
                  background: mode === "otp" ? "#1E3A5F" : "#F8FAFC",
                  color: mode === "otp" ? "#fff" : "#64748B",
                  borderLeft: "1px solid #E2E8F0", transition: "background 0.15s",
                }}>Login with OTP</button>
              </div>

              {/* ── Password form ── */}
              {mode === "password" && (
                <form onSubmit={handlePasswordSubmit}>
                  <div style={{ marginBottom: 14 }}>
                    <label className="form-label">Username</label>
                    <input placeholder="Enter your username" value={username} onChange={(e) => setUsername(e.target.value)} required autoComplete="username" autoFocus />
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <label className="form-label">Password</label>
                    <div className="input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required autoComplete="current-password"
                      />
                      <button type="button" className="input-eye-btn" onClick={() => setShowPassword((v) => !v)} tabIndex={-1}>
                        {showPassword ? "🙈" : "👁️"}
                      </button>
                    </div>
                  </div>
                  <button className="btn" style={{ width: "100%" }} type="submit" disabled={loading}>
                    {loading ? "Signing in..." : "Sign In"}
                  </button>
                  {error && <div className="error-text">{error}</div>}
                </form>
              )}

              {/* ── OTP form ── */}
              {mode === "otp" && (
                <>
                  {otpStep === 1 && (
                    <form onSubmit={handleRequestOtp}>
                      <div style={{ marginBottom: 20 }}>
                        <label className="form-label">Username / Email / Mobile</label>
                        <input placeholder="Enter your username, email or mobile" value={otpUsername} onChange={(e) => setOtpUsername(e.target.value)} required autoFocus />
                      </div>
                      <button className="btn" style={{ width: "100%" }} type="submit" disabled={loading}>
                        {loading ? "Sending OTP..." : "Send OTP"}
                      </button>
                      {error && <div className="error-text">{error}</div>}
                    </form>
                  )}

                  {otpStep === 2 && (
                    <form onSubmit={handleVerifyOtp}>
                      {otpSentTo && (
                        <div style={{ background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 6, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#0369A1" }}>
                          OTP sent to:
                          {otpSentTo.email  && <span style={{ display: "block" }}>📧 {otpSentTo.email}</span>}
                          {otpSentTo.mobile && <span style={{ display: "block" }}>📱 {otpSentTo.mobile}</span>}
                        </div>
                      )}
                      <div style={{ marginBottom: 20 }}>
                        <label className="form-label">Enter 6-digit OTP</label>
                        <input
                          placeholder="• • • • • •" value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          required autoFocus inputMode="numeric"
                          style={{ fontSize: 22, letterSpacing: 8, textAlign: "center" }}
                        />
                        <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--muted)" }}>OTP expires in 10 minutes.</p>
                      </div>
                      <button className="btn" style={{ width: "100%", marginBottom: 10 }} type="submit" disabled={loading || otp.length < 6}>
                        {loading ? "Verifying..." : "Verify & Sign In"}
                      </button>
                      <button type="button" className="btn secondary" style={{ width: "100%", fontSize: 13 }} onClick={handleRequestOtp} disabled={otpTimer > 0 || loading}>
                        {otpTimer > 0 ? `Resend OTP in ${otpTimer}s` : "Resend OTP"}
                      </button>
                      <button type="button" style={{ marginTop: 10, width: "100%", background: "none", border: "none", color: "var(--muted)", fontSize: 13, cursor: "pointer" }}
                        onClick={() => { setOtpStep(1); setOtp(""); setError(""); setInfo(""); }}>
                        ← Change username
                      </button>
                      {error && <div className="error-text">{error}</div>}
                      {info && !error && <div style={{ color: "green", fontSize: 13, marginTop: 8 }}>{info}</div>}
                    </form>
                  )}
                </>
              )}

              <div className="divider" style={{ margin: "20px 0" }} />
              <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
                <p style={{ margin: 0, color: "var(--muted)" }}>
                  New school? <Link to="/register" style={{ fontWeight: 600 }}>Register here →</Link>
                </p>
                <p style={{ margin: 0, color: "var(--muted)" }}>
                  Student or parent? <Link to="/portal" style={{ fontWeight: 600 }}>Go to Student Portal →</Link>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
